/**
 * Servicio para gestión de medicamentos y suplementos
 * Conectado al backend de agthealth-patients
 */

import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Configuración base para las peticiones al API según el entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5004'
  : (process.env.REACT_APP_API_HEALTH_PATIENT_URL);

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';

// URL completa
const API_URL = `${API_BASE}${API_PREFIX}`;

// Configurar axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000
});

// Interceptor para añadir el token de autenticación de Firebase
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===========================================
// SERVICIOS DE MEDICAMENTOS
// ===========================================

const medicationService = {
  /**
   * Obtiene todos los medicamentos
   */
  getAll: async () => {
    try {
      const response = await apiClient.get('/medications');
      return {
        success: true,
        data: response.data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      };
    } catch (error) {
      console.error('Error al obtener medicamentos:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Obtiene un medicamento por ID
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/medications/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener medicamento:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Crea un nuevo medicamento
   */
  create: async (medicationData) => {
    try {
      const response = await apiClient.post('/medications', medicationData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al crear medicamento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Actualiza un medicamento
   */
  update: async (id, medicationData) => {
    try {
      const response = await apiClient.put(`/medications/${id}`, medicationData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al actualizar medicamento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Elimina un medicamento
   */
  delete: async (id) => {
    try {
      await apiClient.delete(`/medications/${id}`);
      return {
        success: true
      };
    } catch (error) {
      console.error('Error al eliminar medicamento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Marca un medicamento como activo/inactivo
   */
  toggleActive: async (id) => {
    try {
      const response = await apiClient.patch(`/medications/${id}/toggle-active`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al cambiar estado del medicamento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }
};

// ===========================================
// SERVICIOS DE SUPLEMENTOS
// ===========================================

const supplementService = {
  /**
   * Obtiene todos los suplementos
   */
  getAll: async () => {
    try {
      const response = await apiClient.get('/supplements');
      return {
        success: true,
        data: response.data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      };
    } catch (error) {
      console.error('Error al obtener suplementos:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Obtiene un suplemento por ID
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/supplements/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener suplemento:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Crea un nuevo suplemento
   */
  create: async (supplementData) => {
    try {
      const response = await apiClient.post('/supplements', supplementData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al crear suplemento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Actualiza un suplemento
   */
  update: async (id, supplementData) => {
    try {
      const response = await apiClient.put(`/supplements/${id}`, supplementData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al actualizar suplemento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Elimina un suplemento
   */
  delete: async (id) => {
    try {
      await apiClient.delete(`/supplements/${id}`);
      return {
        success: true
      };
    } catch (error) {
      console.error('Error al eliminar suplemento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Marca un suplemento como activo/inactivo
   */
  toggleActive: async (id) => {
    try {
      const response = await apiClient.patch(`/supplements/${id}/toggle-active`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al cambiar estado del suplemento:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }
};

export { medicationService, supplementService };
